/**
 * Step 3 — Quiz Activity (Phase 2 task #5).
 *
 * MVP (2026-08-15): ini sekarang quiz PERSONALISASI PERTAMA setelah Profil
 * Anak — langkah Quiz Tema dihapus karena tema PAUD 14 adalah konten
 * premium (funpaper tema), tidak ada di pool MVP funpaper harian.
 * questionOrder tetap 2 agar data audit quiz_answers lama tetap konsisten.
 *
 * Per plan §6.1 task #5 + Doc 04 §7 (revisi plan 21):
 *  - premium: 15 PAUD activity kanonik DB; guest/free: 5 activity dari
 *    Funpaper Harian (1 card per row, full-width, 64px tall)
 *  - Multi-select 3-5 (ACTIVITY_MIN, ACTIVITY_MAX)
 *  - Headline: "Aktivitas apa yang seru?"
 *  - Live counter "X dari 5 dipilih"
 *  - CTA "Lanjut" disabled until ≥3 selected
 *  - Wire to API: POST /v1/quiz/answer (questionKey='activity',
 *    questionOrder=2)
 *  - "Lewati" → write default top-4 popular with skipped=true,
 *    then navigate to /quiz-count
 *
 * Design notes:
 *  - Single source of truth for activity lists is `lib/activity.ts`; the
 *    premium gate is shared with advanced preferences.
 *  - Layout is intentionally different from Quiz Tema: 1 card per row,
 *    full-width, with icon left and label center.
 *  - "Lewati" still writes the default value (top-4 popular) with
 *    `skipped: true` — same convention as Quiz Tema (server's
 *    applyAnswerToPreferences unions in the default for first-time users).
 *  - 5th-tap guard: when at MAX, additional taps show a brief inline
 *    warning instead of silently dropping. User can still tap to deselect.
 *  - currentChild is required for the POST. Defensive redirect to
 *    /child-profile if missing.
 *  - timeSpentMs measured from mount to submit, capped at 10 min to
 *    match server zod cap.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'expo-router';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { AppIcon } from '@/components/app-icon';
import { OnboardingStepShell } from '@/components/onboarding/OnboardingStepShell';
import { Brand, Radii, Spacing } from '@/constants/theme';
import { ApiError } from '@/lib/api/client';
import { activityIcon } from '@/lib/icons';
import { getStoredAccount } from '@/lib/auth';
import { activityOptionsForTier, tierForSession } from '@/lib/premium';
import { isPremiumCached } from '@/lib/premium-status';
import { submitQuizAnswer } from '@/lib/api/quiz';
import { trackAnalyticsEvent } from '@/lib/analytics';
import { useOnboarding } from '@/contexts/onboarding-context';
import {
  ACTIVITY_MIN,
  ACTIVITY_MAX,
  ACTIVITY_DEFAULT_SKIP,
  type Activity,
} from '@/lib/activity';

import { ONBOARDING_ROUTES, getStepByPath } from '@/constants/onboarding';

const WARNING_DURATION_MS = 2_500;
const TIME_SPENT_CAP_MS = 600_000; // matches server zod cap

export default function QuizActivityScreen() {
  const router = useRouter();
  const step = getStepByPath(ONBOARDING_ROUTES.quizActivity);
  const { currentChild } = useOnboarding();
  const account = getStoredAccount();
  const activityOptions = activityOptionsForTier(
    tierForSession({
      isGuest: account ? account.is_guest === true : true,
      isPremium: isPremiumCached(),
    }),
  );

  const [selected, setSelected] = useState<readonly string[]>([]);
  const [warning, setWarning] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mountTimeRef = useRef<number>(Date.now());
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Defensive redirect: this screen needs the child profile from step 2.
  useEffect(() => {
    if (!currentChild) {
      router.replace(ONBOARDING_ROUTES.childProfile);
    }
  }, [currentChild, router]);

  // Cleanup warning timer on unmount.
  useEffect(() => {
    return () => {
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
    };
  }, []);

  const showWarning = useCallback((message: string) => {
    setWarning(message);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    warningTimerRef.current = setTimeout(() => {
      setWarning(null);
      warningTimerRef.current = null;
    }, WARNING_DURATION_MS);
  }, []);

  const toggleActivity = useCallback(
    (activity: Activity) => {
      if (isSubmitting) return;
      setError(null);
      const isSelected = selected.includes(activity.name);
      if (isSelected) {
        setSelected(selected.filter((n) => n !== activity.name));
        setWarning(null);
        return;
      }
      if (selected.length >= ACTIVITY_MAX) {
        showWarning(
          `Maksimum ${ACTIVITY_MAX} aktivitas. Hapus dulu yang lain untuk ganti.`,
        );
        return;
      }
      setSelected([...selected, activity.name]);
    },
    [isSubmitting, selected, showWarning],
  );

  const canSubmit = selected.length >= ACTIVITY_MIN && !isSubmitting;
  const counterText = `${selected.length} dari ${ACTIVITY_MAX} dipilih`;
  const counterHint = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length < ACTIVITY_MIN) {
      return `Pilih minimal ${ACTIVITY_MIN} aktivitas`;
    }
    return null;
  }, [selected.length]);

  const submit = useCallback(
    async (overrideSelected?: readonly string[]) => {
      if (isSubmitting) return;
      if (!currentChild) return; // guarded by useEffect

      const finalSelected = overrideSelected ?? selected;
      if (
        overrideSelected === undefined &&
        finalSelected.length < ACTIVITY_MIN
      ) {
        return; // belt-and-suspenders
      }

      const timeSpentMs = Math.min(
        Math.max(0, Date.now() - mountTimeRef.current),
        TIME_SPENT_CAP_MS,
      );

      setIsSubmitting(true);
      setError(null);
      try {
        await submitQuizAnswer({
          childProfileId: currentChild.id,
          questionKey: 'activity',
          questionOrder: 2,
          answerPayload: {
            selected: [...finalSelected],
            skipped: overrideSelected !== undefined,
            timeSpentMs,
          },
        });
        trackAnalyticsEvent('Onboarding Step Completed', {
          step_name: 'activity_preferences',
          skipped: overrideSelected !== undefined,
          selected_count: finalSelected.length,
        });
        router.push(ONBOARDING_ROUTES.quizCount);
      } catch (err) {
        const message =
          err instanceof ApiError
            ? err.message
            : 'Gagal menyimpan pilihan aktivitas. Coba lagi.';
        setError(message);
        setIsSubmitting(false);
      }
    },
    [currentChild, isSubmitting, router, selected],
  );

  const onKembali = () => router.back();
  const onLewati = () => submit(ACTIVITY_DEFAULT_SKIP);
  const onLanjut = () => submit();

  // Bounce the user if no currentChild (defensive — same as Quiz Tema).
  if (!currentChild) {
    return null;
  }

  return (
    <OnboardingStepShell
      progress={step?.progress ?? 3 / 5}
      stepLabel={step?.stepLabel ?? '3 / 5'}
      onKembali={onKembali}
      onLewati={onLewati}
      onLanjut={onLanjut}
      lanjutDisabled={!canSubmit}
      lanjutLabel={isSubmitting ? 'Menyimpan…' : 'Lanjut'}
    >
      <View style={styles.headerBlock}>
        <ThemedText type="subtitle" style={styles.title}>
          Aktivitas apa yang seru?
        </ThemedText>
        <ThemedText
          type="default"
          themeColor="textSecondary"
          style={styles.subtitle}
        >
          Pilih {ACTIVITY_MIN}-{ACTIVITY_MAX} yang paling cocok. Kamu bisa skip dan pakai
          rekomendasi default.
        </ThemedText>
      </View>

      <View style={styles.listWrap}>
        <ScrollView
          contentContainerStyle={styles.listScroll}
          showsVerticalScrollIndicator={false}
        >
          {activityOptions.map((activity) => {
            const active = selected.includes(activity.name);
            return (
              <Pressable
                key={activity.name}
                onPress={() => toggleActivity(activity)}
                disabled={isSubmitting}
                accessibilityRole="checkbox"
                accessibilityState={{
                  checked: active,
                  disabled: isSubmitting,
                }}
                accessibilityLabel={activity.name}
                style={({ pressed }) => [
                  styles.card,
                  active && styles.cardActive,
                  pressed && !active && styles.cardPressed,
                ]}
              >
                <AppIcon source={activityIcon(activity.name)} size={48} />
                <ThemedText
                  type="default"
                  style={[styles.cardLabel, active && styles.cardLabelActive]}
                  numberOfLines={1}
                >
                  {activity.name}
                </ThemedText>
                {active ? (
                  <View style={styles.checkmarkBadge}>
                    <ThemedText style={styles.checkmarkGlyph}>✓</ThemedText>
                  </View>
                ) : null}
              </Pressable>
            );
          })}
        </ScrollView>
      </View>

      <View style={styles.footer}>
        <View style={styles.counterRow}>
          <ThemedText
            type="small"
            themeColor={warning ? 'danger' : 'textSecondary'}
            style={styles.counterText}
          >
            {warning ?? counterHint ?? counterText}
          </ThemedText>
          {error ? (
            <ThemedText
              type="small"
              style={styles.errorText}
              numberOfLines={2}
            >
              {error}
            </ThemedText>
          ) : null}
        </View>
      </View>
    </OnboardingStepShell>
  );
}

const CARD_BG = '#ffffff';
const CARD_BORDER = '#e5e5e5';
const CARD_BORDER_ACTIVE = Brand.primary;
const CARD_BG_ACTIVE = 'rgba(86, 63, 122, 0.05)';

const styles = StyleSheet.create({
  headerBlock: {
    gap: Spacing.two,
    paddingTop: Spacing.two,
  },
  title: {
    color: Brand.primary,
  },
  subtitle: {
    lineHeight: 22,
  },
  listWrap: {
    flex: 1,
    paddingTop: Spacing.four,
  },
  listScroll: {
    paddingBottom: Spacing.three,
    gap: Spacing.two,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: CARD_BG,
    borderWidth: 1,
    borderColor: CARD_BORDER,
    borderRadius: Radii.medium,
    paddingHorizontal: Spacing.three,
    minHeight: 64,
    gap: Spacing.three,
  },
  cardActive: {
    borderWidth: 2,
    borderColor: CARD_BORDER_ACTIVE,
    backgroundColor: CARD_BG_ACTIVE,
  },
  cardPressed: {
    backgroundColor: 'rgba(86, 63, 122, 0.08)',
  },
  cardLabel: {
    flex: 1,
    fontSize: 17,
    fontWeight: '500',
    color: '#201c23',
  },
  cardLabelActive: {
    color: Brand.primary,
    fontWeight: '600',
  },
  checkmarkBadge: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: Brand.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkmarkGlyph: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 16,
  },
  footer: {
    paddingTop: Spacing.three,
    gap: Spacing.two,
  },
  counterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: Spacing.two,
  },
  counterText: {
    fontWeight: '500',
  },
  errorText: {
    color: Brand.danger,
    flexShrink: 1,
    textAlign: 'right',
  },
});
